<template>
  <Dialog :is-open="isOpen" title="Move Sessions to New Task" width="600px" @close="close">
    <div :class="$style.content">
      <!-- Session Selection -->
      <div :class="$style.section">
        <h3>Select Sessions to Move</h3>
        <div :class="$style.sessionsList">
          <div
            v-for="(session, index) in task.sessions"
            :key="index"
            :class="[
              $style.sessionItem,
              { [$style.selected]: selectedSessions.includes(index) }
            ]"
            @click="toggleSessionSelection(index)"
          >
            <div :class="$style.sessionHeader">
              <div :class="$style.sessionInfo">
                <span :class="$style.sessionNumber">Session {{ index + 1 }}</span>
                <span :class="$style.sessionDuration">
                  {{ formatSessionDuration(session) }}
                </span>
              </div>
              <div :class="$style.checkbox">
                <input
                  type="checkbox"
                  :checked="selectedSessions.includes(index)"
                  @click.stop="toggleSessionSelection(index)"
                />
              </div>
            </div>
            <div :class="$style.sessionTimes">
              <div :class="$style.timeInfo">
                <span :class="$style.timeLabel">Start:</span>
                <span :class="$style.timeValue">{{ formatDateTime(session.startTime) }}</span>
              </div>
              <div v-if="session.endTime" :class="$style.timeInfo">
                <span :class="$style.timeLabel">End:</span>
                <span :class="$style.timeValue">{{ formatDateTime(session.endTime) }}</span>
              </div>
              <div v-else :class="$style.activeSession">Active</div>
            </div>
          </div>
        </div>
      </div>

      <!-- New Task Details -->
      <div :class="$style.section">
        <h3>New Task Details</h3>
        <div :class="$style.formGroup">
          <label :class="$style.label">Task Name:</label>
          <input
            v-model="newTaskName"
            :class="$style.input"
            placeholder="Enter task name"
            @keyup.enter="moveSessions"
          />
        </div>
        <div :class="$style.formGroup">
          <label :class="$style.label">Topic:</label>
          <TopicSelect
            v-model="newTaskTopicId"
            @update:modelValue="updateTopic"
          />
        </div>
        <div :class="$style.formGroup">
          <label :class="$style.label">Estimated Duration:</label>
          <TimeInput
            :duration="newTaskEstimatedDuration"
            @update:duration="updateEstimatedDuration"
          />
        </div>
      </div>

      <!-- Summary -->
      <div v-if="selectedSessions.length > 0" :class="$style.section">
        <h3>Summary</h3>
        <div :class="$style.summary">
          <div :class="$style.summaryItem">
            <span :class="$style.summaryLabel">Sessions to move:</span>
            <span :class="$style.summaryValue">{{ selectedSessions.length }}</span>
          </div>
          <div :class="$style.summaryItem">
            <span :class="$style.summaryLabel">Total duration:</span>
            <span :class="$style.summaryValue">{{ formatDuration(selectedSessionsDuration) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div :class="$style.actions">
      <button :class="$style.cancelButton" @click="close">Cancel</button>
      <button 
        :class="$style.moveButton" 
        :disabled="!canMoveSessions"
        @click="moveSessions"
      >
        Move Sessions
      </button>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import type { Task, WorkSession } from "../types/task";
import Dialog from "./Dialog.vue";
import TopicSelect from "./TopicSelect.vue";
import TimeInput from "./TimeInput.vue";

const props = defineProps<{
  isOpen: boolean;
  task: Task;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "move-sessions", data: {
    sessionIndices: number[];
    newTaskName: string;
    newTaskTopicId: string;
    newTaskEstimatedDuration: number;
  }): void;
}>();

const selectedSessions = ref<number[]>([]);
const newTaskName = ref("");
const newTaskTopicId = ref("other");
const newTaskEstimatedDuration = ref(0);

// Computed properties
const selectedSessionsDuration = computed(() => {
  return selectedSessions.value.reduce((total, index) => {
    const session = props.task.sessions[index];
    if (!session.endTime) {
      // For active sessions, calculate duration up to now
      const currentDuration = new Date().getTime() - new Date(session.startTime).getTime();
      return total + currentDuration;
    }
    const duration = new Date(session.endTime).getTime() - new Date(session.startTime).getTime();
    return total + duration;
  }, 0);
});

const canMoveSessions = computed(() => {
  return selectedSessions.value.length > 0 && newTaskName.value.trim() !== "";
});

// Reset form when dialog opens
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      selectedSessions.value = [];
      newTaskName.value = "";
      newTaskTopicId.value = "other";
      newTaskEstimatedDuration.value = 0;
    }
  }
);

function toggleSessionSelection(index: number) {
  const currentIndex = selectedSessions.value.indexOf(index);
  if (currentIndex > -1) {
    selectedSessions.value.splice(currentIndex, 1);
  } else {
    selectedSessions.value.push(index);
  }
  // Sort the indices to maintain order
  selectedSessions.value.sort((a, b) => a - b);
}

function updateTopic(topicId: string) {
  newTaskTopicId.value = topicId;
}

function updateEstimatedDuration(duration: number) {
  newTaskEstimatedDuration.value = duration;
}

function formatSessionDuration(session: WorkSession): string {
  if (!session.endTime) {
    const currentDuration = new Date().getTime() - new Date(session.startTime).getTime();
    return formatDuration(currentDuration);
  }
  const duration = new Date(session.endTime).getTime() - new Date(session.startTime).getTime();
  return formatDuration(duration);
}

function formatDuration(ms: number): string {
  if (!ms) return "0h 0m 0s";
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);

  let result = "";
  if (hours > 0) {
    result += `${hours}h `;
  }
  if (minutes > 0 || hours > 0) {
    result += `${minutes}m `;
  }
  if (seconds > 0 || (minutes === 0 && hours === 0)) {
    result += `${seconds}s`;
  }

  return result.trim();
}

function formatDateTime(isoString: string): string {
  return new Date(isoString).toLocaleString();
}

function moveSessions() {
  if (!canMoveSessions.value) return;

  emit("move-sessions", {
    sessionIndices: selectedSessions.value,
    newTaskName: newTaskName.value.trim(),
    newTaskTopicId: newTaskTopicId.value,
    newTaskEstimatedDuration: newTaskEstimatedDuration.value,
  });
}

function close() {
  emit("close");
}
</script>

<style module>
.content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 15px;
}

.section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.section h3 {
  margin: 0 0 10px 0;
  font-size: 1em;
  color: rgba(255, 255, 255, 0.9);
}

.sessionsList {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.sessionItem {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 12px;
  border-left: 3px solid var(--primary-color);
  cursor: pointer;
  transition: all 0.2s ease;
}

.sessionItem:hover {
  background: rgba(255, 255, 255, 0.08);
}

.sessionItem.selected {
  background: rgba(var(--primary-color-rgb), 0.2);
  border-left-color: var(--primary-color);
}

.sessionHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.sessionInfo {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sessionNumber {
  font-weight: 600;
  color: var(--primary-color);
}

.sessionDuration {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9em;
}

.checkbox {
  display: flex;
  align-items: center;
}

.checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.sessionTimes {
  font-size: 0.9em;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
}

.timeInfo {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
}

.timeLabel {
  font-weight: 500;
  min-width: 40px;
}

.timeValue {
  color: rgba(255, 255, 255, 0.9);
}

.activeSession {
  color: var(--primary-color);
  font-weight: 500;
}

.formGroup {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.label {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

.input {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 8px 12px;
  color: white;
  font-size: inherit;
  font-family: inherit;
}

.input:focus {
  outline: none;
  border-color: var(--primary-color);
  background: rgba(255, 255, 255, 0.15);
}

.summary {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 12px;
}

.summaryItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.summaryItem:last-child {
  margin-bottom: 0;
}

.summaryLabel {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

.summaryValue {
  font-weight: 600;
  color: white;
}

.actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
}

.cancelButton {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  padding: 8px 16px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancelButton:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}

.moveButton {
  background: var(--primary-color);
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
}

.moveButton:hover:not(:disabled) {
  background: var(--primary-color-hover);
}

.moveButton:disabled {
  background: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
}
</style> 